import {
  keeperCreatePersonalVoice,
  keeperCreateUserAvatarKnowledgeDocument,
  keeperCreateVoiceClone,
  keeperDeletePersonalVoice,
  keeperDeleteUserAvatar,
  keeperDeleteUserAvatarKnowledgeDocument,
  keeperGetUserAvatarKnowledge,
  keeperGetUserAvatarKnowledgeOperation,
  keeperListAvatars,
  keeperListVoiceLibraries,
  keeperUpdatePersonalVoice,
  keeperUpdateUserAvatarKnowledgeDocument,
  livesConfirmSeniorNotice,
  livesGetAvatar,
  livesGetComplianceStatus,
  livesGetLivePlaybackShareDetail,
  livesSubmitEmergencyContact,
  livesVerifyIdentity,
} from '@/app/services/live';
import type {
  LiveV1CreatePersonalVoiceRequest,
  LiveV1CreateVoiceCloneRequest,
  LiveV1SubmitEmergencyContactRequest,
  LiveV1VerifyIdentityRequest,
} from '@/app/services/live/types.gen';

export {
  keeperCreateVoiceClone,
  keeperDeleteUserAvatar,
  keeperGetOrCreateUserAvatar,
  keeperListVoiceLibraries,
  keeperUpdateUserAvatar,
} from '@/app/services/live';

/** keeperDeleteUserAvatar 的薄封装：按 avatar id 删除（归档）当前用户的角色。仅 UGC 角色可删。 */
export function deleteUserAvatar(id: string) {
  return keeperDeleteUserAvatar({ path: { id } });
}

/** keeperCreateVoiceClone 的薄封装：直接收请求体（录制走 audio_base64，上传走 audio_uri，二选一）。 */
export function createVoiceClone(body: LiveV1CreateVoiceCloneRequest) {
  return keeperCreateVoiceClone({ body });
}

interface PagerQuery {
  /** 页码，从 0 开始，默认 0。 */
  page?: number;
  /** 每页数量，默认见各封装。pagesz 建议留 buffer（防空 id 被过滤）。 */
  pagesz?: number;
}
/**
 * 新版个人音色：创建（不绑 avatar，直接进个人音色库，录制走 audio_base64 / 上传走 audio_uri）。
 */
export function createPersonalVoice(body: LiveV1CreatePersonalVoiceRequest) {
  return keeperCreatePersonalVoice({ body });
}

/** 个人音色：重命名（仅改 display_name）。 */
export function updatePersonalVoice(voiceLibraryId: string, displayName: string) {
  return keeperUpdatePersonalVoice({
    path: { voice_library_id: voiceLibraryId },
    body: { voice_library_id: voiceLibraryId, display_name: displayName },
  });
}

/** 个人音色：删除（后端先删千问再软删 DB；被使用中返回 InUse）。 */
export function deletePersonalVoice(voiceLibraryId: string) {
  return keeperDeletePersonalVoice({ path: { voice_library_id: voiceLibraryId } });
}

/** 拉取当前用户的个人音色库（source=user，需登录）。 */
export function listPersonalVoiceLibraries() {
  return keeperListVoiceLibraries({ query: { source: 'user' } });
}

/**
 * UGC 角色（用户自建）。仅 avatar_types=["user"] 分支的响应会填充数量字段
 * （active_avatar_count / max_avatar_count），用于判断是否达到创建上限。
 */
export function listUserAvatars({ page = 0, pagesz = 10 }: PagerQuery = {}) {
  return keeperListAvatars({
    query: {
      'pager.page': page,
      'pager.pagesz': pagesz,
      avatar_types: ['user'],
    },
  });
}

/** PGC 官方预设角色。与 UGC 分开拉取，可独立翻页。 */
export function listPresetAvatars({ page = 0, pagesz = 20 }: PagerQuery = {}) {
  return keeperListAvatars({
    query: {
      'pager.page': page,
      'pager.pagesz': pagesz,
      avatar_types: ['system'],
    },
  });
}

/** 分享落地页：按 avatar id 拉取单个角色（简化版，游客可用）。 */
export async function getPublicAvatar(avatarId: string, accessToken?: string) {
  return livesGetAvatar({
    path: { avatar_id: avatarId },
    ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
  });
}

export async function getPublicLivePlaybackShare(playbackId: string, token?: string) {
  return livesGetLivePlaybackShareDetail({
    path: { id: playbackId },
    query: token ? { token } : undefined,
  });
}

export const getLiveComplianceStatus = () => livesGetComplianceStatus();
export const verifyLiveIdentity = (body: LiveV1VerifyIdentityRequest) => livesVerifyIdentity({ body });
export const submitLiveEmergencyContact = (body: LiveV1SubmitEmergencyContactRequest) =>
  livesSubmitEmergencyContact({ body });
export const confirmLiveSeniorNotice = () => livesConfirmSeniorNotice({ body: {} });

// ========== 知识库管理 ==========

/** 获取用户角色的知识库文档列表（当前为单文档模型）。 */
export function getUserAvatarKnowledge(avatarId: string) {
  return keeperGetUserAvatarKnowledge({ path: { avatar_id: avatarId } });
}

/** 轮询知识库操作状态（创建/更新/删除后调用，直到 succeeded/failed）。 */
export function getUserAvatarKnowledgeOperation(avatarId: string, operationId: string) {
  return keeperGetUserAvatarKnowledgeOperation({
    path: { avatar_id: avatarId, operation_id: operationId },
  });
}

/** 创建知识库文档（上传文件 URI 后调用，返回 operation_id 用于轮询）。 */
export function createUserAvatarKnowledgeDocument(avatarId: string, fileUri: string, fileName: string) {
  return keeperCreateUserAvatarKnowledgeDocument({
    path: { avatar_id: avatarId },
    body: { avatar_id: avatarId, files: [{ file_uri: fileUri, file_name: fileName }] },
  });
}

/** 更新/替换知识库文档（上传新文件 URI 后调用，返回 operation_id 用于轮询）。 */
export function updateUserAvatarKnowledgeDocument(avatarId: string, fileUri: string, fileName: string) {
  return keeperUpdateUserAvatarKnowledgeDocument({
    path: { avatar_id: avatarId },
    body: { avatar_id: avatarId, files: [{ file_uri: fileUri, file_name: fileName }] },
  });
}

/** 删除知识库文档（返回 operation_id 用于轮询）。 */
export function deleteUserAvatarKnowledgeDocument(avatarId: string) {
  return keeperDeleteUserAvatarKnowledgeDocument({
    path: { avatar_id: avatarId },
  });
}
